import { cx } from '@linaria/core';
import { Navbar } from 'components/Navbar';
import { SidebarV2 } from 'components/SidebarV2/Sidebar';
import { useSidebarState } from 'components/SidebarV2/SidebarContext';
import { HomePageFooter } from 'modules/Home/components/HomePageFooter';
import { PageWrapperStyles } from './styles';

export type PageWithSidebarProps = {
  children: React.ReactNode;
  /**
   * Whether to make the top navbar stick to the top of viewport when scrolling
   * @default true
   */
  withStickyNavbar?: boolean;
  /**
   * Additional content to render below the navbar but above the main page content.
   * Useful for content which won't be horizontally placed with the main content (e.g. a banner)
   */
  additionalHeaderContent?: React.ReactNode;
  /**
   * Whether to show default footer at the bottom of the page.
   * @default false
   */
  withFooter?: boolean;
  /**
   * Classname to override ths styles for the default footer element. withFooter must be true for this to have any effect.
   */
  footerClassName?: string;
  /**
   * Custom footer to render at the bottom of the page. If provided, overrides `withFooter` prop.
   */
  customFooter?: React.ReactNode;
  /**
   * Whether to make the page content take the full width of the page.
   */
  fullWidth?: boolean;
};

export const PageWrapper: React.FC<PageWithSidebarProps> = ({
  children,
  withStickyNavbar = true,
  additionalHeaderContent,
  withFooter = false,
  footerClassName,
  customFooter,
  fullWidth = false,
}) => {
  const { isCollapsed } = useSidebarState();
  const defaultFooter = withFooter && <HomePageFooter footerClassName={footerClassName} />;

  return (
    <div className={cx(PageWrapperStyles.layout, isCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded')}>
      <SidebarV2 />

      <div className={PageWrapperStyles.container}>
        <Navbar className={withStickyNavbar ? PageWrapperStyles.navbar : ''} />

        <div className={PageWrapperStyles.contentHorizontalPlacement}>{additionalHeaderContent}</div>

        <main
          id="main-content"
          data-testid="main"
          className={cx('main', PageWrapperStyles.main, PageWrapperStyles.contentHorizontalPlacement, fullWidth && 'page-full-width')}
        >
          {children}
        </main>
        {customFooter || defaultFooter}
      </div>
    </div>
  );
};
